/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { SilentRequest } from '@azure/msal-browser'
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../store/rootStore'
import apiClient from '../services/api-client'
import { passwordResetConfig } from '../features/azure-b2c/config'
import msalInstance from '../features/azure-b2c/msalInstance'
import { useSelector } from 'react-redux'
import { selectAllFarmerAccounts, selectCurrentFarmerAccount } from '../features/azure-b2c/storage'
import AtomicSDK from '@atomic.io/action-cards-web-sdk'
import { clearLocalStorageExceptWhitelist } from '../store/rootReducer'

const useMsalFunctions = () => {
  const { instance, inProgress, accounts } = useMsal()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [msalState, setMsalState] = useState<'login' | 'reset'>('login')

  const farmerAccount = useSelector(selectCurrentFarmerAccount)
  const digitalFarmerState = useSelector(selectAllFarmerAccounts)

  const createTokenRequest = (prompt = 'none') => {
    const accessTokenRequest: SilentRequest = {
      scopes: [`${process.env.REACT_APP_REQUEST_API_SCOPES}`],
      account: accounts[0],
      prompt: prompt,
    }
    return accessTokenRequest
  }
  const closeBrowser = async () => {
    try {
      await Browser.close()
    } catch (error) {
      console.log("Suppressed: Tried to close browser but it wasn't open", error)
    }
  }

  const redirectCallback = useCallback(
    async (url: string) => {
      if (!url) {
        return
      }
      if (Capacitor.isNativePlatform()) {
        await closeBrowser()
      }
      if (msalState === 'login') {
        try {
          const uri = new URL(url)
          const { hash } = uri
          if (hash.includes('AADB2C90118')) {
            setTimeout(handleResetPassword, 1000)
          }
          if (hash.includes('state=')) {
            const slug = `#state=${uri.hash.split('#state=').pop()?.replace('?', '')}`
            if (slug) {
              await handleMsalRedirect(slug)
            }
          }
        } catch (error) {
          console.error('Error handling redirect response:', error)
          await closeBrowser()
        }
      }
      if (msalState === 'reset') {
        resetMsalInstance()
        setTimeout(handleMsalLogin, 1000)
      }
    },
    [instance, msalState],
  )

  const handleMsalRedirect = async (slug: string) => {
    if (slug) {
      await instance
        .handleRedirectPromise(slug)
        .then(async (result) => {
          if (result !== null) {
            await closeBrowser()
          } else {
            console.log('Result is null')
          }
        })
        .catch((err) => {
          console.error('Error in native callback: ')
          console.error(err)
        })
    }
  }

  const handleMsalLogin = async (callback?: () => void) => {
    try {
      setMsalState('login')
      await instance.loginRedirect(createTokenRequest('login'))
      if (callback) {
        callback()
      }
    } catch (e: any) {
      console.error('Login error:', e)
      if (e.message.includes('AADB2C90118')) {
        setTimeout(handleResetPassword, 250)
      } else if (e.errorCode === 'user_cancelled') {
        resetMsalInstance()
      }
    }
  }

  const handleMsalLogout = async () => {
    try {
      await AtomicSDK.logout()
      dispatch({
        type: 'LOGOUT_REQUEST',
      })
      await instance.logoutRedirect()
      apiClient.tokenInterceptorId = null
      resetMsalInstance()
      navigate('/login', { replace: true })
      window.location.reload()
    } catch (e) {
      console.error('Logout error:', e)
    }
  }

  const handleResetPassword = async () => {
    try {
      setMsalState('reset')
      await instance.loginRedirect(passwordResetConfig)
    } catch (e: any) {
      console.error('Password reset error:', e)
      if (e.errorCode === 'user_cancelled') {
        resetMsalInstance()
      }
    }
  }

  const resetMsalInstance = () => {
    setMsalState('login')
    instance.getActiveAccount() && instance.setActiveAccount(null)
    instance.clearCache()
    clearLocalStorageExceptWhitelist()
    sessionStorage.clear()
    msalInstance.clearCache()
  }

  // useEffect for In-App Browser
  // useEffect(() => {
  //   let timeout: string | number | NodeJS.Timeout | undefined
  //   Browser.addListener('browserFinished', () => {
  //     timeout = setTimeout(() => {
  //       if (
  //         accounts.length <= 0 &&
  //         !window.location.hash.includes('state') &&
  //         inProgress === 'login'
  //       ) {
  //         resetMsalInstance()
  //         window.location.reload()
  //       }
  //     }, 500)
  //   })
  //   return () => {
  //     Browser.removeAllListeners()
  //     clearTimeout(timeout)
  //   }
  // }, [accounts, inProgress])

  //useEffect for Capacitor Native
  useEffect(() => {
    const handleUrlOpen = async (event: { url: string }) => {
      await redirectCallback(event.url)
    }
    const appUrlOpenListener = App.addListener('appUrlOpen', handleUrlOpen)
    return () => {
      appUrlOpenListener.then((listener) => {
        listener?.remove()
      })
    }
  }, [redirectCallback])

  return {
    instance,
    inProgress,
    accounts,
    createTokenRequest,
    handleMsalLogin,
    handleMsalLogout,
    resetMsalInstance,
    isAuthenticated: !!farmerAccount,
    isShareholder: farmerAccount?.hasShareholderAccess ?? false,
    hasShareholder: digitalFarmerState?.hasShareholderAccounts ?? false,
  }
}

export default useMsalFunctions
